import React, { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { userApi } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

const DOT = { success: "bg-emerald-500", error: "bg-rose-500", warning: "bg-amber-500", info: "bg-sky-500" };
// These are triggered by the user's own action and already confirmed by an on-page toast
const SELF_INITIATED = ["Deposit request received", "Exchange completed", "Welcome aboard"];

export default function NotificationBell() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const seen = useRef(null);
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => userApi.get("/notifications").then((r) => r.data),
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
    staleTime: 5000,
  });
  const items = data?.items || [];
  const unread = data?.unread || 0;

  // Toast newly-arrived notifications (status changes, credits) while the user is on any page
  useEffect(() => {
    if (!data) return;
    const ids = new Set(items.map((i) => i.id));
    if (seen.current) {
      items.filter((i) => !seen.current.has(i.id) && !i.read && !SELF_INITIATED.includes(i.title)).slice(0, 3).forEach((i) => {
        const fn = i.type === "success" ? toast.success : i.type === "error" ? toast.error : toast.info;
        fn(i.title, { description: i.message });
      });
      if (items.some((i) => !seen.current.has(i.id))) {
        qc.invalidateQueries({ queryKey: ["wallets"] });
        qc.invalidateQueries({ queryKey: ["deposits"] });
        qc.invalidateQueries({ queryKey: ["ledger"] });
      }
    }
    seen.current = ids;
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const readAll = async () => {
    await userApi.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };
  const open = async (n) => {
    if (!n.read) await userApi.post(`/notifications/${n.id}/read`);
    qc.invalidateQueries({ queryKey: ["notifications"] });
    if (n.link) nav(n.link);
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--accent))] px-1 text-[10px] font-semibold text-slate-900">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[340px] p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-semibold">Notifications</p>
          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={readAll} data-testid="notifications-mark-all-read">
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </Button>
        </div>
        <ScrollArea className="h-[360px]">
          {items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">You're all caught up.</p>}
          {items.map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid="notification-item" className={cn("flex w-full gap-3 border-b px-4 py-3 text-left transition-colors hover:bg-secondary", !n.read && "bg-sky-50/60")}>
              <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", DOT[n.type] || DOT.info)} />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{n.title}</span>
                <span className="block text-xs text-muted-foreground">{n.message}</span>
                <span className="mt-1 block text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</span>
              </span>
            </button>
          ))}
        </ScrollArea>
        <button onClick={() => nav("/notifications")} className="w-full border-t py-2.5 text-center text-xs font-medium text-primary hover:bg-secondary" data-testid="notifications-view-all">
          View all notifications
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
